import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleActiveTab, setSorting, setFilters, clearFilters, setKeyword, setSearchValue, DEFAULT_PROMO_FILTERS } from '../../store/promoSlice.js';
import {
    setEditedEvent,
    setEditModalMode,
    setModalViewActualTab,
    setAddEventMode,
    toggleEditModal,
} from '../../store/eventsSlice.js';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import PromotionOrderPanel from './paymentTest2';
import './Promo.css';
import Table from '../../components/list2/table';
import { getHeaders, headerWidths } from './promoHistoryTableConfig';
import FilterBar from '../../components/components/filter_bar/FilterBar.jsx';
import FilterPanel from '../../components/components/filter_panel/FilterPanel.jsx';
import { fetchPromotions, mapPromotionsToRows } from './promoTableService.js';
import ApiService from '../../components/api_services/apiService.jsx';
import { showAlert } from '../../components/components/global_alert/alertBus';
import { mapApiPlansToCards, renderPlanDescription } from './promoPlansUiConfig.jsx';
import { PricingPlanCard } from './PricingPlanCard.jsx';
import SchedulePromotionModal from './SchedulePromotionModal.jsx';

function Promo() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const activeTab = useSelector((state) => state.promo.activeTab);
    const checkedItems = useSelector((state) => state.promo.checkedItems);
    const sortBy = useSelector((state) => state.promo.sortBy);
    const sortDirection = useSelector((state) => state.promo.sortDirection);
    const currentPage = useSelector((state) => state.promo.currentPage);
    const keyword = useSelector((state) => state.promo.keyword);
    const searchValue = useSelector((state) => state.promo.searchValue);
    const filters = useSelector((state) => state.promo.filters) ?? DEFAULT_PROMO_FILTERS;
    const totalCount = useSelector((state) => state.promo.totalCount);
    const editModalOpen = useSelector((state) => state.events.editModalOpen);
    const sortArrowColorActive = '#060606';
    const sortArrowColorInactive = '#06060650';

    const [rawPromotions, setRawPromotions] = useState([]);
    const [isOrderPanelOpen, setIsOrderPanelOpen] = useState(false);
    const [selectedPlanType, setSelectedPlanType] = useState('Basic');
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [pricingPlans, setPricingPlans] = useState([]);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [scheduleModalPromotion, setScheduleModalPromotion] = useState(null);

    useEffect(() => {
        if (activeTab !== 'Informacje') return;

        const loadPlans = async () => {
            try {
                const plans = await ApiService.getPromotionPlans();
                setPricingPlans(mapApiPlansToCards(plans));
            } catch (error) {
                setPricingPlans(mapApiPlansToCards([]));
            }
        };

        loadPlans();
    }, [activeTab]);

    const handleRenewPromotion = useCallback((promo) => {
        setSelectedPlanType(promo.promotionType);
        setSelectedEventId(promo.eventId);
        setSelectedInvoiceData(promo.invoiceData || null);
        setIsOrderPanelOpen(true);
    }, []);

    const handleSchedulePromotion = useCallback((promo) => {
        setScheduleModalPromotion(promo);
    }, []);

    const handleDeletePromotion = useCallback(async (promo) => {
        if (!window.confirm('Czy na pewno chcesz usunąć tę promocję?')) {
            return;
        }
        try {
            await ApiService.deletePromotion(promo.id);
            showAlert('Promocja została usunięta.');
            setRefreshKey((value) => value + 1);
        } catch (error) {
            showAlert('Nie udało się usunąć promocji.');
        }
    }, []);

    const handleOpenEventDetails = useCallback((promo) => {
        if (!promo.eventId) {
            return;
        }
        dispatch(setAddEventMode(false));
        dispatch(setModalViewActualTab('Dane podstawowe'));
        dispatch(setEditModalMode('preview'));
        dispatch(setEditedEvent(promo.eventId));
        if (!editModalOpen) {
            dispatch(toggleEditModal());
        }
        navigate('/');
    }, [dispatch, editModalOpen, navigate]);

    const rows = useMemo(
        () => mapPromotionsToRows(rawPromotions, checkedItems, dispatch, {
            onRenew: handleRenewPromotion,
            onSchedule: handleSchedulePromotion,
            onDelete: handleDeletePromotion,
            onOpenEvent: handleOpenEventDetails,
        }),
        [rawPromotions, checkedItems, dispatch, handleRenewPromotion, handleSchedulePromotion, handleDeletePromotion, handleOpenEventDetails]
    );

    useEffect(() => {
        if (activeTab !== 'Historia') return;
        fetchPromotions(currentPage, sortBy, sortDirection, keyword, filters, dispatch, setRawPromotions);
    }, [activeTab, currentPage, sortBy, sortDirection, keyword, filters, dispatch, refreshKey]);

    const headers = getHeaders(
        sortBy,
        sortDirection,
        dispatch,
        setSorting,
        sortArrowColorActive,
        sortArrowColorInactive,
        rows,
        checkedItems,
    );

    const handleSidebarClick = (tabName) => {
        dispatch(toggleActiveTab(tabName));
    };

    const handlePromoteClick = (planName) => {
        setSelectedPlanType(planName);
        setSelectedEventId(null);
        setSelectedInvoiceData(null);
        setIsOrderPanelOpen(true);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        dispatch(setSearchValue(value));
        if (value.length >= 3) {
            dispatch(setKeyword(value));
        } else {
            dispatch(setKeyword(null));
        }
    };

    const handleFilterClick = () => {
        setIsFilterPanelOpen(true);
    };

    const handleCloseFilterPanel = () => {
        setIsFilterPanelOpen(false);
    };

    const handleApplyFilters = (newFilters) => {
        dispatch(setFilters(newFilters));
        setIsFilterPanelOpen(false);
    };

    const handleClearFilters = () => {
        dispatch(clearFilters());
    };

    const handleRemoveFilter = (filterKey) => {
        dispatch(setFilters({ ...filters, [filterKey]: '' }));
    };

    const getActiveFilterCount = () => (
        Object.values(filters).filter((value) => value && value !== '').length
    );

    const filterChips = [];
    if (filters.dateFrom) {
        filterChips.push({ key: 'dateFrom', label: `Od: ${filters.dateFrom}`, value: filters.dateFrom });
    }
    if (filters.dateTo) {
        filterChips.push({ key: 'dateTo', label: `Do: ${filters.dateTo}`, value: filters.dateTo });
    }
    if (filters.promotionType) {
        filterChips.push({ key: 'promotionType', label: filters.promotionType, value: filters.promotionType });
    }
    if (filters.status) {
        const statusLabels = {
            active: 'Aktywne',
            scheduled: 'Zaplanowane',
            pending: 'Oczekujące',
            finished: 'Zakończone',
            cancelled: 'Anulowane',
        };
        filterChips.push({
            key: 'status',
            label: statusLabels[filters.status] || filters.status,
            value: filters.status,
        });
    }

    const promoFilterFields = [
        [
            { key: 'dateFrom', label: 'Data od', type: 'date' },
            { key: 'dateTo', label: 'Data do', type: 'date' },
        ],
        [
            { key: 'promotionType', label: 'Rodzaj promowania', type: 'select', optionsKey: 'promotionTypes', placeholder: 'Wybierz' },
            { key: 'status', label: 'Status', type: 'select', optionsKey: 'status', placeholder: 'Wszystkie' },
        ],
    ];

    const promoFilterOptions = {
        promotionTypes: [
            { value: '', label: 'Wybierz' },
            { value: 'Basic', label: 'Basic' },
            { value: 'Standard', label: 'Standard' },
            { value: 'Visibility+', label: 'Visibility+' },
            { value: 'Premium', label: 'Premium' },
        ],
        status: [
            { value: '', label: 'Wszystkie' },
            { value: 'active', label: 'Aktywne' },
            { value: 'scheduled', label: 'Zaplanowane' },
            { value: 'pending', label: 'Oczekujące' },
            { value: 'finished', label: 'Zakończone' },
            { value: 'cancelled', label: 'Anulowane' },
        ],
    };

    return (
        <div>
            <Menu />
            <Content>
                {isOrderPanelOpen ? (
                    <PromotionOrderPanel
                        initialPlan={selectedPlanType}
                        initialEventId={selectedEventId}
                        initialInvoiceData={selectedInvoiceData}
                        onBack={() => {
                            setIsOrderPanelOpen(false);
                            setSelectedEventId(null);
                            setSelectedInvoiceData(null);
                        }}
                        onSuccess={() => setRefreshKey((value) => value + 1)}
                    />
                ) : (
                    <div className="promo-container">
                        <div className="promo-header">
                            <h1>Promowanie wydarzeń</h1>
                        </div>

                        <div className="promo-content">
                            <div className="promo-sidebar">
                                <div
                                    className={`sidebar-item ${activeTab === 'Informacje' ? 'active' : ''}`}
                                    onClick={() => handleSidebarClick('Informacje')}
                                >
                                    Informacje <span className="right-oriented">&#10217;</span>
                                </div>
                                <div
                                    className={`sidebar-item ${activeTab === 'Historia' ? 'active' : ''}`}
                                    onClick={() => handleSidebarClick('Historia')}
                                >
                                    Historia promowania <span className="right-oriented">&#10217;</span>
                                </div>
                            </div>

                            {activeTab === 'Informacje' && (
                                <div className="promo-main">
                                    <h2>Wybierz swój plan promowania</h2>
                                    <p>Wypromuj swoje wydarzenie i dotrzyj do większej liczby odbiorców.</p>
                                    <div className="pricing-cards">
                                        {pricingPlans.map((plan) => (
                                            <PricingPlanCard
                                                key={plan.id}
                                                plan={{
                                                    ...plan,
                                                    descriptionParts: renderPlanDescription(plan.description),
                                                }}
                                                onPromote={handlePromoteClick}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'Historia' && (
                                <div className="promo-main promo-history-main">
                                    <div className="promo-history-table-container">
                                        <FilterBar
                                            filters={filters}
                                            filterChips={filterChips}
                                            searchValue={searchValue}
                                            onSearchChange={handleSearchChange}
                                            resultsCount={totalCount}
                                            showFilterButton
                                            showResultsCount
                                            onFilterClick={handleFilterClick}
                                            onClearFilters={handleClearFilters}
                                            onRemoveFilter={handleRemoveFilter}
                                            activeFilterCount={getActiveFilterCount()}
                                        />
                                        <Table
                                            headers={headers}
                                            rows={rows}
                                            columnWidths={headerWidths}
                                            idViewing={false}
                                            idArrayId={1}
                                            cellClass2="cell-center"
                                            cellClass2ConditionList={[4, 5, 6, 7]}
                                            tableClass="promo-history-table"
                                            tableContainerClass="promo-history-table-inner"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Content>
            <FilterPanel
                isOpen={isFilterPanelOpen}
                onClose={handleCloseFilterPanel}
                onApplyFilters={handleApplyFilters}
                fields={promoFilterFields}
                options={promoFilterOptions}
            />
            <SchedulePromotionModal
                isOpen={Boolean(scheduleModalPromotion)}
                promotion={scheduleModalPromotion}
                onClose={() => setScheduleModalPromotion(null)}
                onSuccess={() => setRefreshKey((value) => value + 1)}
            />
        </div>
    );
}

export default Promo;
